import React, { ChangeEvent, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import CloseIcon from '@mui/icons-material/Close'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import { LoadingButton } from '@mui/lab'
import { Box, IconButton, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import { useMutation } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import { Excel } from 'assets/icons/app'
import { AxiosError, AxiosResponse } from 'axios'
import { FaberResponse } from 'helpers/backendTypes'
import OrbitSession from 'hooks/useCookies'

import { BootstrapDialog, BootstrapDialogTitle } from '../../../../common/AbstractModal/BootstrapDialog'

export default function AddDistributorPromoDialog() {
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [isCsvUploading, setIsCsvUploading] = useState<boolean>(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const {
    mutate: uploadCsv,
    isLoading: isSubmittingCsv
  } = useMutation(PaymentAPI.uploadDistributorPromocodeCsv, {
    onSuccess: (res: AxiosResponse<FaberResponse>) => {
      const message = res.data.message
        ? res.data.message
        : 'Successfully processed the csv file in the server'
      setCsvFile(null)
      setOpenDialog(false)
      toast.success(message)
    },
    onError: (err: AxiosError<FaberResponse>) => {
      const message = err.response?.data?.message
        ? err.response.data.message
        : 'There was a problem trying to process the csv file. Try reuploading csv file or contacting the devs.'
      toast.error(message)
    },
  })

  const handleOpenDialog = () => setOpenDialog(true)
  const handleCloseDialog = () => {
    setCsvFile(null)
    setOpenDialog(false)
  }

  const handleRemoveCsvClick = () => {
    setCsvFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUploadCsvClick = (e: ChangeEvent<HTMLInputElement>) => {
    setIsCsvUploading(true)
    const file = e.target.files
    if (file && file.length > 0) {
      setCsvFile(file[0])
    }
    setIsCsvUploading(false)
  }

  const faberToken = OrbitSession.get('faberToken')
  const handleSubmitCsvClick = () => {
    if (csvFile) {
      const formData = new FormData()
      formData.append('file', csvFile)
      uploadCsv({
        faberToken,
        uploadObject: formData
      })
    }
  }

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 B';
    const sizes = ['B', 'kB', 'MB', 'GB', 'TB'];
    const index = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = parseFloat((bytes / Math.pow(1024, index)).toFixed(decimals))
    return `${value} ${sizes[index]}`
  }

  return (
    <div>
      {/* Open dialog button */}
      <Button variant='outlined' onClick={handleOpenDialog}>
        Upload csv
      </Button>
      <BootstrapDialog
        maxWidth='sm'
        onClose={handleCloseDialog}
        open={openDialog}
      >
        <BootstrapDialogTitle onClose={handleCloseDialog}>
          Upload csv
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <Typography variant='h4' gutterBottom>
            WARNING: Uploading a csv will generate a promo code and send an email to the associated email address for each row.
          </Typography>
          <LoadingButton
            variant='outlined'
            component='label'
            startIcon={<FileUploadIcon />}
            loading={isCsvUploading}
            sx={{ p: 3, background: 'white', width: '100%', margin: '24px 0 0 0' }}
          >
            Upload CSV
            <input
              ref={fileInputRef}
              type='file'
              hidden
              accept='.csv'
              onChange={handleUploadCsvClick}
            />
          </LoadingButton>
          {csvFile && (
            <Box>
              <Box alignItems='center' display='flex' flexDirection='row' justifyContent='space-between'
                margin='24px 0' sx={{ border: '1px solid', borderColor: (theme) => theme.palette.grey[500], borderRadius: '8px' }}>
                <Box alignItems='center' display='flex' justifyContent='center' margin='24px'>
                  <Excel height='24' width='24' />
                </Box>
                <Box alignItems='flex-start' display='flex' flexDirection='column' width='100%'>
                  <Typography margin='0'>
                    {csvFile.name}
                  </Typography>
                  <Typography margin='0'>
                    {formatBytes(csvFile.size)}
                  </Typography>
                </Box>
                <IconButton
                  aria-label='close'
                  onClick={handleRemoveCsvClick}
                  sx={{
                    color: (theme) => theme.palette.grey[500]
                  }}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <LoadingButton
                variant='contained'
                loading={isSubmittingCsv}
                onClick={handleSubmitCsvClick}
                sx={{ p: 3, width: '100%', margin: '0' }}
              >
                Submit
              </LoadingButton>
            </Box>
          )}
        </DialogContent>
      </BootstrapDialog>
    </div>
  )
}
